/// <reference types="vite/client" />

import { useState, useCallback } from 'react';
import type { InspectResponse, MediaCandidate } from '@excalibur/shared/types';

const API_BASE = import.meta.env.VITE_API_BASE || '';

function App() {
  const [url, setUrl] = useState('');
  const [result, setResult] = useState<InspectResponse | null>(null);
  const [polling, setPolling] = useState(false);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!url.trim()) return;

    setResult(null);
    try {
      const res = await fetch(`${API_BASE}/api/inspect`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: url.trim() }),
      });
      const data = await res.json() as InspectResponse;
      if (!res.ok) throw new Error(data.error || 'Request failed');
      setResult(data);
      if (data.status !== 'completed' && data.status !== 'failed') {
        setPolling(true);
        pollJob(data.jobId);
      }
    } catch (err) {
      setResult({ jobId: '', status: 'failed', originalUrl: url, finalUrl: null, contentType: null, title: null, description: null, imageUrl: null, mediaCandidates: [], selectedMedia: null, downloadUrl: null, error: err instanceof Error ? err.message : 'Unknown error', sizeBytes: null, sourceStatus: null });
    }
  }, [url]);

  const pollJob = useCallback(async (jobId: string) => {
    while (polling) {
      await new Promise(r => setTimeout(r, 2000));
      try {
        const res = await fetch(`${API_BASE}/api/jobs/${jobId}`);
        const data = await res.json() as InspectResponse;
        setResult(data);
        if (data.status === 'completed' || data.status === 'failed') {
          setPolling(false);
          break;
        }
      } catch {
        setPolling(false);
        break;
      }
    }
  }, [polling]);

  const handleDownload = useCallback(async (jobId: string, filename?: string) => {
    try {
      const res = await fetch(`${API_BASE}/api/download/${jobId}`);
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename || `download-${jobId.slice(0, 8)}`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Download failed');
    }
  }, []);

  const formatBytes = (bytes: number | null) => {
    if (!bytes) return 'Unknown size';
    const units = ['B', 'KB', 'MB', 'GB'];
    let i = 0;
    let size = bytes;
    while (size >= 1024 && i < units.length - 1) { size /= 1024; i++; }
    return `${size.toFixed(1)} ${units[i]}`;
  };

  const getStatusIcon = (status: InspectResponse['status']) => {
    switch (status) {
      case 'pending': return '⏳';
      case 'fetching': return '📥';
      case 'extracting': return '🔍';
      case 'storing': return '💾';
      case 'completed': return '✅';
      case 'failed': return '❌';
    }
  };

  const statusLabels: Record<InspectResponse['status'], string> = {
    pending: 'Waiting...',
    fetching: 'Fetching URL...',
    extracting: 'Extracting media...',
    storing: 'Storing file...',
    completed: 'Ready',
    failed: 'Failed',
  };

  return (
    <div className="container">
      <header>
        <h1 className="logo">Excalibur</h1>
        <p className="tagline">Extract and download media from any public URL</p>
      </header>

      <form onSubmit={handleSubmit} className="card">
        <div className="form-group">
          <label htmlFor="url">URL</label>
          <input
            id="url"
            type="url"
            value={url}
            onChange={e => setUrl(e.target.value)}
            placeholder="https://example.com/video.mp4 or https://site.com/page"
            required
            autoComplete="off"
          />
        </div>
        <button type="submit" className="btn btn-primary" style={{ width: '100%' }} disabled={polling}>
          {polling ? 'Processing...' : 'Inspect & Extract'}
        </button>
      </form>

      {result && (
        <div className="card result">
          <div className={`status ${result.status}`}>
            <span className="sr-only">{statusLabels[result.status]}</span>
            <span className="spinner" style={{ display: ['pending','fetching','extracting','storing'].includes(result.status) ? 'block' : 'none' }} />
            <span>{getStatusIcon(result.status)} {statusLabels[result.status]}</span>
          </div>

          {result.error && (
            <div className="error-box" role="alert">{result.error}</div>
          )}

          {(result.title || result.imageUrl || result.selectedMedia) && (
            <div className="result-header">
              {result.imageUrl && (
                <img className="result-thumb" src={result.imageUrl} alt="" loading="lazy" />
              )}
              <div className="result-info">
                {result.title && <h2 className="result-title">{result.title}</h2>}
                <div className="result-meta">
                  {result.finalUrl && result.finalUrl !== result.originalUrl && (
                    <span>🔗 <a href={result.finalUrl} target="_blank" rel="noopener noreferrer">Final URL</a></span>
                  )}
                  {result.contentType && <span>📄 {result.contentType}</span>}
                  {result.sizeBytes && <span>📦 {formatBytes(result.sizeBytes)}</span>}
                  {result.sourceStatus && <span>📡 HTTP {result.sourceStatus}</span>}
                </div>
                {result.description && <p className="result-desc">{result.description}</p>}
              </div>
            </div>
          )}

          {result.selectedMedia && (
            <div className="media-preview">
              {result.selectedMedia.type === 'video' && (
                <video controls src={result.selectedMedia.url} preload="metadata" />
              )}
              {result.selectedMedia.type === 'audio' && (
                <audio controls src={result.selectedMedia.url} preload="metadata" />
              )}
              {result.selectedMedia.type === 'image' && (
                <img src={result.selectedMedia.url} alt={result.title || 'Extracted image'} />
              )}
            </div>
          )}

          {result.mediaCandidates.length > 0 && (
            <div className="candidates">
              <h3 className="candidates-title">Found {result.mediaCandidates.length} media candidate(s)</h3>
              <div className="candidate-list">
                {result.mediaCandidates.map((c, i) => (
                  <div key={i} className="candidate-item">
                    <span className={`candidate-type ${c.type}`}>{c.type[0].toUpperCase()}</span>
                    <span className="candidate-url" title={c.url}>{c.url}</span>
                    {c.size && <span className="candidate-size">{formatBytes(c.size)}</span>}
                    {c.mimeType && <span className="candidate-size">{c.mimeType}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {result.downloadUrl && (
            <div className="actions">
              <button className="btn btn-primary" onClick={() => handleDownload(result.jobId)}>
                ⬇ Download
              </button>
              <button className="btn btn-secondary" onClick={() => navigator.clipboard.writeText(result.downloadUrl!)}>
                📋 Copy Link
              </button>
              <a className="btn btn-secondary" href={result.downloadUrl} target="_blank" rel="noopener noreferrer">
                🔗 Open Direct
              </a>
            </div>
          )}
        </div>
      )}

      <footer className="footer">
        <p>Built with Cloudflare Workers, Pages, D1, R2, KV & Workers AI</p>
        <p>
          <a href="https://github.com" target="_blank" rel="noopener noreferrer">GitHub</a>
          <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy</a>
          <a href="/terms" target="_blank" rel="noopener noreferrer">Terms</a>
        </p>
        <p>Only processes publicly accessible content. No authentication bypass, DRM circumvention, or private content access.</p>
      </footer>
    </div>
  );
}

export default App;